import { z } from "zod";
import { availabilityStatusSchema, signupStatusSchema } from "./statusSchemas";

// Registration event sub-schema
//
// KB-2673 — `whenIso` and `zoneLabel` are carried through the form UNCHANGED.
// They are scraper-owned provenance, not operator-editable fields: `whenIso` is
// the provider's stated wall-clock with its offset, and `zoneLabel` is the
// provider's own word for the zone. Neither is rendered by this form.
//
// They must still round-trip. zod strips unknown keys on parse, so omitting them
// here silently DESTROYED both on any admin save: an operator opening a session
// to fix a typo in the title would wipe the deadline, and because `regEvents` is
// frozen the next cadence run does not restore it — it raises a proposal a human
// must approve. Optional (never defaulted) so an event that never had them does
// not gain empty strings.
const registrationEventSchema = z.object({
  eventKey: z.string(),
  when: z.date().optional(),
  wasTimeProvided: z.boolean().default(false),
  note: z.string().default(""),
  whenIso: z.string().optional(),
  zoneLabel: z.string().optional()
});

const pricingTierPriceOverridesSchema = z.object({
  // Back-compat / UX: allow missing v from form state, default to 1
  v: z.literal(1).optional().default(1),
  tier2: z.number().min(0).max(99999).nullable().optional(),
  tier3: z.number().min(0).max(99999).nullable().optional()
}).transform((val) => ({
  ...val,
  v: 1 as const
}));

// Define the schema for offer form values
export const offerFormSchema = z.object({
  displayTitle: z.string().optional(),
  description: z.string().max(2000).optional(),
  eligibilityNotes: z.string().optional(),  // New field
  status: z.string().optional(),
  offeringTypeKeys: z.array(z.string()).optional(),
  priceRegular: z.number().nullable().optional(),
  variationId: z.string().optional(),
  variationKey: z.string().optional(), // Added for variation key
  myOfferIncrementKey: z.string().nullable().optional(), // Added for storing selected increment option
  isVisible: z.boolean().optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  priceEarlyBird: z.number().nullable().optional(),
  earlyBirdDeadline: z.date().nullable().optional(),
  priceOverride: z.number().nullable().optional(), // Added for price override
  numDaysOverride: z.number().nullable().optional(), // Added for numDays override
  priceNoteOverride: z.string().nullable().optional(), // Added for price note override
  timingNoteOverride: z.string().nullable().optional(), // Added for timing note override
  ageMin: z.number().nullable().optional(),
  ageMax: z.number().nullable().optional(),
  daysOfWeek: z.record(z.string().optional()).optional(),
  availabilityStatus: availabilityStatusSchema.optional(),
  signupStatus: signupStatusSchema.optional(),
  currentSalePrice: z.number().nullable().optional(),
  notes: z.string().optional(),
  visibility: z.string().optional(), // Added for visibility
  variation: z.any().optional(), // Added explicitly for variation object
  url: z.string().url().optional().or(z.literal("")),
  regUrl: z.string().url().optional().or(z.literal("")),  // New field (program registration URL)
  regEvents: z.array(registrationEventSchema).optional()
    .transform((events) => {
      if (!events) return undefined;
      
      // Filter out events without dates during validation
      const validEvents = events.filter(e => e.when !== undefined && e.when !== null);
      return validEvents.length > 0 ? validEvents : undefined;
    })
    .refine((events) => {
      if (!events || events.length === 0) return true;
      
      // Validate unique keys for specific event types (only for events with dates)
      const uniqueKeys = ["e_posted", "e_open", "e_closes"];
      for (const uniqueKey of uniqueKeys) {
        const count = events.filter(e => e.eventKey === uniqueKey).length;
        if (count > 1) return false;
      }
      return true;
    }, {
      message: "Each of e_posted, e_open, and e_closes can appear at most once"
    })

  ,
  pricingTierPriceOverrides: pricingTierPriceOverridesSchema.nullable().optional(),
  // Session-level age override controls (UI-only)
  overrideAges: z.boolean().optional().default(false),
  minAgeInYearsOverride: z.number().nullable().optional(),
  maxAgeInYearsOverride: z.number().nullable().optional(),
  minAgeGradeKeyOverride: z.string().optional(),
  maxAgeGradeKeyOverride: z.string().optional(),
  minAgeGradeValueOverride: z.number().nullable().optional(),
  maxAgeGradeValueOverride: z.number().nullable().optional(),
  showMinAgeGradeAsPrimaryOverride: z.boolean().optional(),
  showMaxAgeGradeAsPrimaryOverride: z.boolean().optional(),
  minAgeNoteOverride: z.string().optional(),
  maxAgeNoteOverride: z.string().optional()
});

export type OfferFormValues = z.infer<typeof offerFormSchema>;
export type RegistrationEventFormValues = z.infer<typeof registrationEventSchema>;

/**
 * KB-2673 — map an offer's stored `regEvents` into form values, preserving the
 * scraper-owned time provenance.
 *
 * This exists as a named function rather than an inline `.map()` because the
 * inline version was the bug: it listed four fields explicitly, so `whenIso` and
 * `zoneLabel` were dropped the moment a session was opened in the editor, and
 * nothing failed loudly. The consequence is invisible — marketing's deadline line
 * simply disappears (`deriveDeadline` returns undefined when `parseIsoParts`
 * cannot read `whenIso`), with no error anywhere and no cadence-run repair,
 * because `regEvents` is a frozen field whose changes go to review.
 *
 * Anything added to `RegistrationEvent` in future must be carried here too.
 */
export function toFormRegEvents(
  regEvents: Array<Record<string, unknown>> | undefined | null,
  ensureValidDate: (v: unknown) => Date | undefined,
): RegistrationEventFormValues[] {
  if (!Array.isArray(regEvents)) return [];
  return regEvents.map((event) => {
    const whenIso = typeof event.whenIso === "string" ? event.whenIso.trim() : "";
    const zoneLabel = typeof event.zoneLabel === "string" ? event.zoneLabel.trim() : "";
    return {
      eventKey: String(event.eventKey ?? ""),
      when: event.when instanceof Date ? event.when : ensureValidDate(event.when),
      wasTimeProvided: event.wasTimeProvided === true,
      note: typeof event.note === "string" ? event.note : "",
      // Absent stays absent — an event that never carried these must not gain
      // empty strings, which would themselves be a (silent) content change.
      ...(whenIso ? { whenIso } : {}),
      ...(zoneLabel ? { zoneLabel } : {}),
    } as RegistrationEventFormValues;
  });
}
